// File: frontend/src/components/upload/PolicySummaryCard.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/formatters';
import {
  CheckCircle2, Shield, Car, Home, Heart, Umbrella, Scale, Briefcase,
  Hash, Building2, Calendar, DollarSign, User, MapPin, Eye,
  ChevronRight, FileText, Users, AlertTriangle, Zap, Upload,
  MessageSquare, ClipboardList, Receipt
} from 'lucide-react';
import styles from './PolicySummaryCard.module.css';

const PolicySummaryCard = ({ metadata, fileInfo, onUploadAnother }) => {
  const navigate = useNavigate();

  if (!metadata) return null;

  // ── Helpers ──
  const formatType = (type) => {
    if (!type) return 'N/A';
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const titleCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getCurrencyForCountry = (country) => {
    const map = {
      'TR': 'TRY', 'US': 'USD', 'CA': 'CAD', 'GB': 'GBP',
      'AU': 'AUD', 'NZ': 'NZD', 'JP': 'JPY', 'CN': 'CNY',
      'IN': 'INR', 'BR': 'BRL', 'MX': 'MXN', 'CH': 'CHF',
      'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR',
      'NL': 'EUR', 'BE': 'EUR', 'AT': 'EUR', 'IE': 'EUR',
      'PT': 'EUR', 'FI': 'EUR', 'GR': 'EUR',
    };
    return map[(country || '').toUpperCase()] || 'USD';
  };

  const formatCurrency = (value) => {
    if (value == null) return null;
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : Number(value);
    if (isNaN(num)) return value;
    const country = (metadata.policy_metadata?.country || '').toUpperCase();
    const currency = getCurrencyForCountry(country);
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `${num.toLocaleString()}`;
    }
  };

  const getPolicyTypeDisplay = (type) => {
    const map = {
      auto: 'Auto Insurance', auto_policy: 'Auto Insurance',
      home: 'Home Insurance', homeowners: 'Homeowners Insurance',
      renters: 'Renters Insurance', health: 'Health Insurance',
      life: 'Life Insurance', umbrella: 'Umbrella Policy',
      liability: 'Liability Insurance', commercial: 'Commercial Insurance',
      business: 'Business Insurance', condo: 'Condo Insurance',
      flood: 'Flood Insurance', fire: 'Fire Insurance',
    };
    return map[(type || '').toLowerCase()] || formatType(type) || 'Insurance Policy';
  };

  const getPolicyTypeIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('auto') || t.includes('vehicle') || t.includes('car')) return <Car size={20} />;
    if (t.includes('home') || t.includes('property') || t.includes('dwelling') || t.includes('condo')) return <Home size={20} />;
    if (t.includes('health') || t.includes('medical') || t.includes('life')) return <Heart size={20} />;
    if (t.includes('umbrella')) return <Umbrella size={20} />;
    if (t.includes('liability')) return <Scale size={20} />;
    if (t.includes('business') || t.includes('commercial')) return <Briefcase size={20} />;
    return <Shield size={20} />;
  };

  const getStatusConfig = (status) => {
    if (!status) return { label: 'Unknown', className: styles.statusUnknown };
    const s = status.toLowerCase();
    if (s === 'active' || s === 'in_force') return { label: 'Active', className: styles.statusActive };
    if (s === 'expired') return { label: 'Expired', className: styles.statusExpired };
    if (s.includes('cancel')) return { label: 'Cancelled', className: styles.statusCancelled };
    if (s === 'pending') return { label: 'Pending', className: styles.statusPending };
    return { label: formatType(status), className: styles.statusUnknown };
  };

  const formatAddress = (addr) => {
    if (!addr) return null;
    if (typeof addr === 'string') return titleCase(addr);
    const parts = [
      addr.street ? titleCase(addr.street) : null,
      addr.city ? titleCase(addr.city) : null,
      addr.province_state ? titleCase(addr.province_state) : null,
      addr.postal_zip_code,
      addr.country ? addr.country.toUpperCase() : null,
    ].filter(Boolean);
    return parts.join(', ') || null;
  };

  // ── Extract data ──
  const policyMeta = metadata.policy_metadata || {};
  const insuranceCompany = metadata.insurance_company || {};
  const insuredInfo = metadata.insured_information || {};
  const primaryInsured = insuredInfo.primary_insured || {};
  const coveragesData = metadata.coverages || {};
  const coveragesList = coveragesData.coverages || [];
  const premiumBilling = metadata.premium_billing || {};
  const vehiclesData = metadata.vehicles || {};
  const vehiclesList = vehiclesData.vehicles || [];
  const propertiesData = metadata.properties || {};
  const propertiesList = propertiesData.properties || [];
  const driversData = metadata.drivers || {};
  const driversList = driversData.drivers || [];
  const additionalDetails = metadata.additional_details || {};
  const extractionConfidence = metadata.extraction_confidence || {};
  const sectionsExtracted = metadata.sections_extracted || [];
  const documentType = metadata.document_type || policyMeta.policy_type || '';

  const policyStatus = policyMeta.policy_status || '';
  const statusConfig = getStatusConfig(policyStatus);
  const sectionCount = Array.isArray(sectionsExtracted) ? sectionsExtracted.length : 0;
  const confidenceScore = extractionConfidence.overall_confidence || null;
  const exclusions = additionalDetails.exclusions || [];

  // ── Quick stats ──
  const stats = [];
  if (coveragesList.length > 0) {
    stats.push({ icon: <Shield size={16} />, label: coveragesList.length === 1 ? 'Coverage' : 'Coverages', value: coveragesList.length });
  }
  if (vehiclesList.length > 0) {
    stats.push({ icon: <Car size={16} />, label: vehiclesList.length === 1 ? 'Vehicle' : 'Vehicles', value: vehiclesList.length });
  }
  if (propertiesList.length > 0) {
    stats.push({ icon: <Home size={16} />, label: propertiesList.length === 1 ? 'Property' : 'Properties', value: propertiesList.length });
  }
  if (driversList.length > 0) {
    stats.push({ icon: <Users size={16} />, label: driversList.length === 1 ? 'Driver' : 'Drivers', value: driversList.length });
  }
  if (exclusions.length > 0) {
    stats.push({ icon: <AlertTriangle size={16} />, label: 'Exclusions', value: exclusions.length });
  }

  const handleViewDetails = () => {
    if (fileInfo?.file_id) {
      navigate(`/dashboard/policy-details/${fileInfo.file_id}`);
    }
  };

  return (
    <div className={styles.container}>
      {/* ── Success Banner ── */}
      <div className={styles.successBanner}>
        <div className={styles.successIconWrap}>
          <CheckCircle2 size={32} strokeWidth={2.5} />
        </div>
        <div className={styles.successContent}>
          <h2 className={styles.successTitle}>Policy Analyzed Successfully</h2>
          <p className={styles.successSubtitle}>
            We extracted {sectionCount} section{sectionCount !== 1 ? 's' : ''} from your document
            {fileInfo?.file_name && (
              <span className={styles.fileName}> — {fileInfo.file_name}</span>
            )}
          </p>
          {/* {confidenceScore && (
            <div className={styles.confidenceBadge}>
              <Zap size={12} />
              <span>{confidenceScore}% confidence</span>
            </div>
          )} */}
        </div>
      </div>

      {/* ── Policy Header Card ── */}
      <div className={styles.headerCard}>
        <div className={styles.headerTop}>
          <div className={styles.policyTypeTag}>
            {getPolicyTypeIcon(documentType)}
            <span>{getPolicyTypeDisplay(documentType)}</span>
          </div>
          {policyStatus && (
            <span className={`${styles.statusBadge} ${statusConfig.className}`}>
              {statusConfig.label}
            </span>
          )}
        </div>

        <div className={styles.detailsGrid}>
          {policyMeta.policy_number && (
            <div className={styles.detailItem}>
              <Hash size={14} className={styles.detailItemIcon} />
              <div>
                <span className={styles.detailLabel}>Policy Number</span>
                <span className={styles.detailValue}>{policyMeta.policy_number}</span>
              </div>
            </div>
          )}

          {insuranceCompany.company_name && (
            <div className={styles.detailItem}>
              <Building2 size={14} className={styles.detailItemIcon} />
              <div>
                <span className={styles.detailLabel}>Insurer</span>
                <span className={styles.detailValue}>{insuranceCompany.company_name}</span>
              </div>
            </div>
          )}

          {primaryInsured.full_name && (
            <div className={styles.detailItem}>
              <User size={14} className={styles.detailItemIcon} />
              <div>
                <span className={styles.detailLabel}>Policyholder</span>
                <span className={styles.detailValue}>{titleCase(primaryInsured.full_name)}</span>
              </div>
            </div>
          )}

          {(policyMeta.effective_date || policyMeta.expiry_date) && (
            <div className={styles.detailItem}>
              <Calendar size={14} className={styles.detailItemIcon} />
              <div>
                <span className={styles.detailLabel}>Policy Period</span>
                <span className={styles.detailValue}>
                  {policyMeta.effective_date ? formatDate(policyMeta.effective_date, 'long') : '—'}
                  {' → '}
                  {policyMeta.expiry_date ? formatDate(policyMeta.expiry_date, 'long') : '—'}
                </span>
              </div>
            </div>
          )}

          {premiumBilling.total_premium && (
            <div className={styles.detailItem}>
              <DollarSign size={14} className={styles.detailItemIcon} />
              <div>
                <span className={styles.detailLabel}>Total Premium</span>
                <span className={styles.detailValue}>
                  {formatCurrency(premiumBilling.total_premium)}
                  {premiumBilling.payment_frequency && (
                    <span className={styles.frequency}> / {premiumBilling.payment_frequency}</span>
                  )}
                </span>
              </div>
            </div>
          )}

          {primaryInsured.address && formatAddress(primaryInsured.address) && (
            <div className={styles.detailItem}>
              <MapPin size={14} className={styles.detailItemIcon} />
              <div>
                <span className={styles.detailLabel}>Address</span>
                <span className={styles.detailValue}>{formatAddress(primaryInsured.address)}</span>
              </div>
            </div>
          )}
        </div>

        {stats.length > 0 && (
          <div className={styles.statsRow}>
            {stats.map((stat, idx) => (
              <div key={idx} className={styles.statPill}>
                {stat.icon}
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        )}

        {coveragesList.length > 0 && (
          <div className={styles.coveragePreview}>
            <div className={styles.coveragePreviewHeader}>
              <span className={styles.coveragePreviewTitle}>Coverage Highlights</span>
            </div>
            <div className={styles.coveragePreviewGrid}>
              {coveragesList.slice(0, 4).map((cov, idx) => (
                <div key={idx} className={styles.coveragePreviewItem}>
                  <span className={styles.coveragePreviewName}>
                    {cov.coverage_name || formatType(cov.coverage_type) || `Coverage ${idx + 1}`}
                  </span>
                  {cov.limit != null && (
                    <span className={styles.coveragePreviewLimit}>
                      {formatCurrency(cov.limit)}
                    </span>
                  )}
                </div>
              ))}
              {coveragesList.length > 4 && (
                <div className={styles.coveragePreviewMore}>
                  +{coveragesList.length - 4} more coverage{coveragesList.length - 4 !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        )}

        {vehiclesList.length > 0 && (
          <div className={styles.vehiclePreview}>
            {vehiclesList.map((vehicle, idx) => (
              <div key={idx} className={styles.vehiclePreviewItem}>
                <Car size={16} className={styles.vehiclePreviewIcon} />
                <div>
                  <span className={styles.vehiclePreviewName}>
                    {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || `Vehicle ${idx + 1}`}
                  </span>
                  {vehicle.vin && (
                    <span className={styles.vehiclePreviewVin}>VIN: {vehicle.vin}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {propertiesList.length > 0 && (
          <div className={styles.propertyPreview}>
            {propertiesList.map((property, idx) => (
              <div key={idx} className={styles.propertyPreviewItem}>
                <Home size={16} className={styles.propertyPreviewIcon} />
                <div>
                  <span className={styles.propertyPreviewName}>
                    {formatType(property.property_type) || `Property ${idx + 1}`}
                  </span>
                  {property.address && formatAddress(property.address) && (
                    <span className={styles.propertyPreviewAddress}>
                      {formatAddress(property.address)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Action Buttons ── */}
      <div className={styles.actionButtons}>
        <button className={styles.viewDetailsBtn} onClick={handleViewDetails}>
          <Eye size={18} />
          <span>View Full Policy Details</span>
          <ChevronRight size={16} />
        </button>
        <button className={styles.uploadAnotherBtn} onClick={onUploadAnother}>
          <Upload size={16} />
          <span>Upload Another</span>
        </button>
      </div>
    </div>
  );
};

export default PolicySummaryCard;